import { useCallback, useEffect, useState } from 'react';
import type React from 'react';

export interface UseStepIdEditorResult {
  /** The current edit-buffer value (controlled-input bound). */
  draft: string;
  /** Controlled `onChange` for the input. */
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Commit the draft (used as `onBlur`). */
  handleBlur: () => void;
  /** Enter commits, Escape reverts. Other keys pass through. */
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Owns the local edit-buffer for the "Step id" rename input in the node
 * side panel. The buffer resets whenever the underlying node id changes
 * (selecting a different node, rename committed externally, etc.).
 *
 * Commit semantics: trimmed, non-empty, distinct from current id. Otherwise
 * the buffer is reverted to match the canonical id.
 */
export const useStepIdEditor = (
  currentId: string | undefined,
  onRename: (oldId: string, newId: string) => void,
): UseStepIdEditorResult => {
  const [draft, setDraft] = useState<string>(currentId ?? '');

  useEffect(() => {
    setDraft(currentId ?? '');
  }, [currentId]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
  }, []);

  const handleBlur = useCallback(() => {
    if (currentId === undefined) return;
    const trimmed = draft.trim();
    if (trimmed && trimmed !== currentId) {
      onRename(currentId, trimmed);
    } else {
      setDraft(currentId);
    }
  }, [currentId, draft, onRename]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (currentId === undefined) return;
      if (event.key === 'Enter') {
        const trimmed = draft.trim();
        if (trimmed && trimmed !== currentId) {
          onRename(currentId, trimmed);
        } else {
          setDraft(currentId);
        }
        event.currentTarget.blur();
      } else if (event.key === 'Escape') {
        setDraft(currentId);
        event.currentTarget.blur();
      }
    },
    [currentId, draft, onRename],
  );

  return { draft, handleChange, handleBlur, handleKeyDown };
};
